import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiService } from '../services/apiService';
import { showToast } from '../components/Toast';
import ConfirmModal from '../components/ConfirmModal';
import DeveloperBottomNav from '../components/DeveloperBottomNav';
import './BadgeManagement.css';
import { DSIcon, DSInput, DSSelect, DSTextarea } from '../design-system/primitives';
import BadgeIcon from '../components/BadgeIcon';

// Admin-only badge catalog: create/edit badge definitions that either get
// awarded manually (from a user's Manage User page) or auto-awarded by
// criteria evaluation (see server/src/services/badgeEvaluator.js).
// badge_key is an internal slug the server derives from the name - admins
// never see or set it (see server/src/routes/badges.js generateUniqueBadgeKey).

// Auto-awarded criteria types that fire at a count threshold (criteria_value).
// `noun` is what's being counted, used in the threshold label and summary.
const COUNT_CRITERIA = {
  task_completion_count: { option: 'Automatic - task completion count', noun: 'completed task' },
  maintenance_record_count: { option: 'Automatic - machine maintenance count', noun: 'completed maintenance checklist' }
};
const isCountCriteria = (type) => Object.prototype.hasOwnProperty.call(COUNT_CRITERIA, type);

const EMPTY_FORM = { name: '', description: '', icon: '', criteriaType: 'manual', criteriaValue: '' };

function BadgeManagement() {
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newBadge, setNewBadge] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [confirmState, setConfirmState] = useState(null);
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);

  const loadBadges = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getBadgeDefinitions();
      setBadges(response.data || []);
      setError('');
    } catch (err) {
      setError('Failed to load badges.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBadges();
  }, [loadBadges]);

  const activeBadges = useMemo(() => badges.filter((b) => b.is_active), [badges]);
  const inactiveBadges = useMemo(() => badges.filter((b) => !b.is_active), [badges]);

  const handleDeactivate = (badge) => {
    setConfirmState({
      title: 'Deactivate Badge?',
      message: `"${badge.name}" will be hidden from the award picker and the catalog. Members who already earned it keep it. You can restore it later.`,
      confirmLabel: 'Deactivate',
      danger: true,
      onConfirm: async () => {
        setConfirmSubmitting(true);
        try {
          await apiService.updateBadgeDefinition(badge.id, { isActive: false });
          setConfirmState(null);
          showToast(`"${badge.name}" deactivated.`, 'success');
          loadBadges();
        } catch (err) {
          showToast('Failed to deactivate badge.', 'error');
        } finally {
          setConfirmSubmitting(false);
        }
      }
    });
  };

  const handleReactivate = async (badge) => {
    try {
      await apiService.updateBadgeDefinition(badge.id, { isActive: true });
      showToast(`"${badge.name}" restored.`, 'success');
      loadBadges();
    } catch (err) {
      showToast('Failed to restore badge.', 'error');
    }
  };

  const startEdit = (badge) => {
    setEditingId(badge.id);
    setEditForm({
      name: badge.name || '',
      description: badge.description || '',
      icon: badge.icon || '',
      criteriaType: badge.criteria_type,
      criteriaValue: badge.criteria_value ?? ''
    });
  };

  const handleSaveEdit = async () => {
    if (!editForm.name.trim()) {
      showToast('Name is required.', 'error');
      return;
    }
    setSaving(true);
    try {
      await apiService.updateBadgeDefinition(editingId, {
        name: editForm.name,
        description: editForm.description,
        icon: editForm.icon,
        criteriaValue: isCountCriteria(editForm.criteriaType) && editForm.criteriaValue !== ''
          ? parseInt(editForm.criteriaValue, 10)
          : null
      });
      showToast('Badge saved.', 'success');
      setEditingId(null);
      loadBadges();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to save badge.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAddBadge = async () => {
    if (!newBadge.name.trim()) {
      showToast('Name is required.', 'error');
      return;
    }
    if (isCountCriteria(newBadge.criteriaType) && !newBadge.criteriaValue) {
      showToast('A completion count threshold is required for this criteria type.', 'error');
      return;
    }
    setSaving(true);
    try {
      await apiService.createBadgeDefinition({
        name: newBadge.name.trim(),
        description: newBadge.description,
        icon: newBadge.icon,
        criteriaType: newBadge.criteriaType,
        criteriaValue: isCountCriteria(newBadge.criteriaType) ? parseInt(newBadge.criteriaValue, 10) : null
      });
      showToast('Badge created.', 'success');
      setNewBadge(EMPTY_FORM);
      setShowAddForm(false);
      loadBadges();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to create badge.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const describeCriteria = (badge) => {
    if (badge.criteria_type === 'manual') {
      return 'Awarded manually by an admin';
    }
    if (isCountCriteria(badge.criteria_type)) {
      const { noun } = COUNT_CRITERIA[badge.criteria_type];
      return `Auto-awarded at ${badge.criteria_value} ${noun}${badge.criteria_value === 1 ? '' : 's'}`;
    }
    return badge.criteria_type;
  };

  return (
    <div className="badge-mgmt bn-page-padding">
      <div className="bm-header">
        <div className="bm-header__top">
          <div>
            <h1 className="bm-header__title">Badge Catalog</h1>
            <p className="bm-header__subtitle">{activeBadges.length} active badge{activeBadges.length === 1 ? '' : 's'}</p>
          </div>
          <button type="button" className="bm-add-btn" onClick={() => setShowAddForm(true)}>
            + Add Badge
          </button>
        </div>
        <p className="bm-header__hint">
          To give a badge to a specific member, open their profile under User Management and use the Badges section there.
        </p>
      </div>

      {error && <div className="error-message">{error}</div>}

      {!loading && (
        <>
          {showAddForm && (
            <div className="bm-form bm-form--new">
              <p className="bm-form__heading">New Badge</p>
              <label className="bm-field-label">Name</label>
              <DSInput
                type="text"
                className="bm-input"
                value={newBadge.name}
                onChange={(e) => setNewBadge((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Event Champion"
              />
              <label className="bm-field-label">Description</label>
              <DSTextarea
                className="bm-textarea"
                value={newBadge.description}
                onChange={(e) => setNewBadge((p) => ({ ...p, description: e.target.value }))}
                placeholder="What does earning this badge mean?"
              />
              <label className="bm-field-label">Icon (emoji)</label>
              <DSInput
                type="text"
                className="bm-input bm-input--icon"
                value={newBadge.icon}
                onChange={(e) => setNewBadge((p) => ({ ...p, icon: e.target.value }))}
                placeholder="🏆"
              />
              <label className="bm-field-label">Award Method</label>
              <DSSelect
                className="bm-input"
                value={newBadge.criteriaType}
                onChange={(e) => setNewBadge((p) => ({ ...p, criteriaType: e.target.value }))}
              >
                <option value="manual">Manual (admin awards it)</option>
                {Object.entries(COUNT_CRITERIA).map(([type, { option }]) => (
                  <option key={type} value={type}>{option}</option>
                ))}
              </DSSelect>
              {isCountCriteria(newBadge.criteriaType) && (
                <>
                  <label className="bm-field-label">Count Threshold</label>
                  <DSInput
                    type="number"
                    min="1"
                    className="bm-input"
                    value={newBadge.criteriaValue}
                    onChange={(e) => setNewBadge((p) => ({ ...p, criteriaValue: e.target.value }))}
                    placeholder="e.g. 10"
                  />
                </>
              )}
              <div className="bm-form__actions">
                <button
                  type="button"
                  className="bm-btn-primary"
                  onClick={handleAddBadge}
                  disabled={saving || !newBadge.name.trim()}
                >
                  Add Badge
                </button>
                <button type="button" className="bm-btn-secondary" onClick={() => { setShowAddForm(false); setNewBadge(EMPTY_FORM); }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          <p className="bm-section-heading">Active Badges</p>
          <div className="bm-item-list">
            {activeBadges.map((badge) => {
              const isEditing = editingId === badge.id;

              return (
                <div key={badge.id} className="bm-item">
                  {isEditing ? (
                    <div className="bm-form">
                      <p className="bm-form__heading">Edit Badge</p>
                      <label className="bm-field-label">Name</label>
                      <DSInput
                        type="text"
                        className="bm-input"
                        value={editForm.name}
                        onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                      />
                      <label className="bm-field-label">Description</label>
                      <DSTextarea
                        className="bm-textarea"
                        value={editForm.description}
                        onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))}
                      />
                      <label className="bm-field-label">Icon (emoji)</label>
                      <DSInput
                        type="text"
                        className="bm-input bm-input--icon"
                        value={editForm.icon}
                        onChange={(e) => setEditForm((p) => ({ ...p, icon: e.target.value }))}
                      />
                      {isCountCriteria(editForm.criteriaType) && (
                        <>
                          <label className="bm-field-label">Count Threshold</label>
                          <DSInput
                            type="number"
                            min="1"
                            className="bm-input"
                            value={editForm.criteriaValue}
                            onChange={(e) => setEditForm((p) => ({ ...p, criteriaValue: e.target.value }))}
                          />
                        </>
                      )}
                      <div className="bm-form__actions">
                        <button type="button" className="bm-btn-primary" onClick={handleSaveEdit} disabled={saving}>
                          Save
                        </button>
                        <button type="button" className="bm-btn-secondary" onClick={() => setEditingId(null)}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="bm-item__row">
                      <div className="bm-item__icon"><BadgeIcon badge={badge} size={32} /></div>
                      <div className="bm-item__body">
                        <div className="bm-item__name">{badge.name}</div>
                        {badge.description && <div className="bm-item__description">{badge.description}</div>}
                        <div className="bm-item__criteria">{describeCriteria(badge)}</div>
                      </div>
                      <div className="bm-item__actions">
                        <button type="button" className="bm-item__icon-btn" onClick={() => startEdit(badge)} title="Edit"><DSIcon name="edit" /></button>
                        <button type="button" className="bm-item__icon-btn bm-item__icon-btn--danger" onClick={() => handleDeactivate(badge)} title="Deactivate"><DSIcon name="x" /></button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {activeBadges.length === 0 && <p className="bm-empty">No active badges yet. Add one to get started.</p>}
          </div>

          {inactiveBadges.length > 0 && (
            <>
              <p className="bm-section-heading">Deactivated</p>
              <div className="bm-inactive-list">
                {inactiveBadges.map((badge) => (
                  <div key={badge.id} className="bm-inactive-row">
                    <span className="bm-inactive-row__icon"><BadgeIcon badge={badge} size={18} /></span>
                    <p className="bm-inactive-row__title">{badge.name}</p>
                    <button type="button" className="bm-inactive-row__restore" onClick={() => handleReactivate(badge)}>
                      Restore
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {confirmState && (
        <ConfirmModal
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          danger={confirmState.danger}
          submitting={confirmSubmitting}
          onConfirm={confirmState.onConfirm}
          onCancel={() => setConfirmState(null)}
        />
      )}

      <DeveloperBottomNav />
    </div>
  );
}

export default BadgeManagement;
